import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import ArticuloService from '../../services/articulos';
import CategoriaService from '../../services/categorias';

const offerTypes = [
  { value: 'percentage', label: 'Porcentaje de descuento' },
  { value: 'fixed_amount', label: 'Monto fijo de descuento' },
  { value: '2x1', label: '2x1 - Lleva 2 paga 1' },
  { value: 'buy_x_get_y', label: 'Compra X y lleva Y' }
];

const appliesToOptions = [
  { value: 'all', label: 'Todos los productos' },
  { value: 'specific_articles', label: 'Artículos específicos' },
  { value: 'category', label: 'Por categoría' },
  { value: 'store', label: 'Por tienda' }
];

const OfferForm = ({ offer, store, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    code: '',
    type: 'percentage',
    discount_percentage: 0,
    discount_amount: 0,
    min_quantity: 0,
    min_purchase_amount: 0,
    max_discount_amount: 0,
    buy_quantity: 0,
    get_quantity: 0,
    applies_to: 'all',
    article_ids: [],
    category_ids: [],
    store_ids: store ? [store._id] : [],
    excluded_article_ids: [],
    skus: [],
    min_price: 0,
    max_price: 0,
    excluded_skus: [],
    start_date: new Date(),
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // +7 días
    active: true,
    priority: 1,
    stackable: false,
    max_uses_total: 0,
    max_uses_per_user: 0,
    apply_automatically: true
  });

  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);

  useEffect(() => {
    if (offer) {
      setFormData({
        ...offer,
        article_ids: offer.article_ids || [],
        category_ids: offer.category_ids || [],
        store_ids: offer.store_ids || (store ? [store._id] : []),
        excluded_article_ids: offer.excluded_article_ids || [],
        skus: offer.skus || [],
        excluded_skus: offer.excluded_skus || [],
        start_date: new Date(offer.start_date),
        end_date: new Date(offer.end_date)
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer]);

  useEffect(() => {
    if (store && (formData.applies_to === 'specific_articles' || formData.applies_to === 'category')) {
      loadArticles();
      loadCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, formData.applies_to]);

  const loadArticles = async () => {
    if (!store) return;
    setLoadingArticles(true);
    try {
      const data = await ArticuloService.getAllArticlesInStore(store._id);
      setArticles(data || []);
    } catch (err) {
      console.error('Error loading articles:', err);
    } finally {
      setLoadingArticles(false);
    }
  };

  const loadCategories = async () => {
    setLoadingCategories(true);
    try {
      const data = await CategoriaService.getAll();
      setCategories(data || []);
    } catch (err) {
      console.error('Error loading categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (!formData.name.trim()) {
      alert('El nombre es requerido');
      return;
    }

    if (formData.type === 'percentage' && (formData.discount_percentage <= 0 || formData.discount_percentage > 100)) {
      alert('El porcentaje de descuento debe estar entre 1 y 100');
      return;
    }

    if (formData.type === 'fixed_amount' && formData.discount_amount <= 0) {
      alert('El monto de descuento debe ser mayor a 0');
      return;
    }

    if (formData.start_date >= formData.end_date) {
      alert('La fecha de inicio debe ser anterior a la fecha de fin');
      return;
    }

    onSave(formData);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={{
          p: 3,
          maxWidth: 800,
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        <Typography variant='h5' sx={{ mb: 3, fontWeight: 600 }}>
          {offer ? 'Editar Oferta' : 'Nueva Oferta'}
        </Typography>

        {/* Información básica */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Nombre de la oferta *'
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder='Ej: Black Friday 2025'
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label='Descripción'
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder='Descripción detallada de la oferta'
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Código de cupón'
              value={formData.code}
              onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
              placeholder='BF2025'
              helperText='Opcional: código para aplicar manualmente'
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label='Tipo de oferta *'
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value)}
            >
              {offerTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Descuento según tipo */}
          {formData.type === 'percentage' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='number'
                label='Porcentaje de descuento *'
                value={formData.discount_percentage}
                onChange={(e) => handleChange('discount_percentage', parseFloat(e.target.value) || 0)}
                InputProps={{
                  endAdornment: <InputAdornment position='end'>%</InputAdornment>
                }}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
              />
            </Grid>
          )}

          {formData.type === 'fixed_amount' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type='number'
                label='Monto de descuento *'
                value={formData.discount_amount}
                onChange={(e) => handleChange('discount_amount', parseInt(e.target.value) || 0)}
                InputProps={{
                  startAdornment: <InputAdornment position='start'>$</InputAdornment>
                }}
                inputProps={{ min: 0 }}
              />
            </Grid>
          )}

          {(formData.type === '2x1' || formData.type === 'buy_x_get_y') && (
            <>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  type='number'
                  label='Compra cantidad'
                  value={formData.buy_quantity}
                  onChange={(e) => handleChange('buy_quantity', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  type='number'
                  label='Lleva cantidad'
                  value={formData.get_quantity}
                  onChange={(e) => handleChange('get_quantity', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Reglas de aplicación */}
        <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
          Reglas de aplicación
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Cantidad mínima'
              value={formData.min_quantity}
              onChange={(e) => handleChange('min_quantity', parseInt(e.target.value) || 0)}
              helperText='0 = sin mínimo'
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Monto mínimo de compra'
              value={formData.min_purchase_amount}
              onChange={(e) => handleChange('min_purchase_amount', parseFloat(e.target.value) || 0)}
              InputProps={{
                startAdornment: <InputAdornment position='start'>$</InputAdornment>
              }}
              helperText='0 = sin mínimo'
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Descuento máximo'
              value={formData.max_discount_amount}
              onChange={(e) => handleChange('max_discount_amount', parseInt(e.target.value) || 0)}
              InputProps={{
                startAdornment: <InputAdornment position='start'>$</InputAdornment>
              }}
              helperText='0 = sin límite'
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Prioridad'
              value={formData.priority}
              onChange={(e) => handleChange('priority', parseInt(e.target.value) || 1)}
              helperText='Mayor número = mayor prioridad'
              inputProps={{ min: 1 }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Ámbito de aplicación */}
        <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
          Ámbito de aplicación
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label='Aplica a *'
              value={formData.applies_to}
              onChange={(e) => handleChange('applies_to', e.target.value)}
            >
              {appliesToOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {formData.applies_to === 'specific_articles' && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={articles}
                getOptionLabel={(option) => `${option.article.name} (${option.sku || 'Sin SKU'})`}
                value={articles.filter(a => (formData.article_ids || []).includes(a._id))}
                onChange={(e, newValue) => handleChange('article_ids', newValue.map(v => v._id))}
                loading={loadingArticles}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Artículos'
                    placeholder='Selecciona artículos'
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.article.name}
                      {...getTagProps({ index })}
                      size='small'
                    />
                  ))
                }
              />
            </Grid>
          )}

          {formData.applies_to === 'category' && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={categories}
                getOptionLabel={(option) => option.name}
                value={categories.filter(c => (formData.category_ids || []).includes(c._id))}
                onChange={(e, newValue) => handleChange('category_ids', newValue.map(v => v._id))}
                loading={loadingCategories}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Categorías'
                    placeholder='Selecciona categorías'
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      {...getTagProps({ index })}
                      size='small'
                    />
                  ))
                }
              />
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Vigencia */}
        <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
          Vigencia
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label='Fecha de inicio *'
              value={formData.start_date}
              onChange={(newValue) => handleChange('start_date', newValue)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label='Fecha de fin *'
              value={formData.end_date}
              onChange={(newValue) => handleChange('end_date', newValue)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Límites de uso */}
        <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
          Límites de uso
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Usos totales máximos'
              value={formData.max_uses_total}
              onChange={(e) => handleChange('max_uses_total', parseInt(e.target.value) || 0)}
              helperText='0 = ilimitado'
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Usos por usuario'
              value={formData.max_uses_per_user}
              onChange={(e) => handleChange('max_uses_per_user', parseInt(e.target.value) || 0)}
              helperText='0 = ilimitado'
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Opciones adicionales */}
        <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
          Opciones adicionales
        </Typography>

        <Box display='flex' flexDirection='column' gap={1}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) => handleChange('active', e.target.checked)}
                color='success'
              />
            }
            label='Oferta activa'
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.apply_automatically}
                onChange={(e) => handleChange('apply_automatically', e.target.checked)}
                color='primary'
              />
            }
            label='Aplicar automáticamente (sin código)'
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.stackable}
                onChange={(e) => handleChange('stackable', e.target.checked)}
                color='secondary'
              />
            }
            label='Combinable con otras ofertas'
          />
        </Box>

        {/* Botones */}
        <Box display='flex' justifyContent='flex-end' gap={2} mt={4}>
          <Button
            variant='outlined'
            onClick={onCancel}
            sx={{ minWidth: 120 }}
          >
            Cancelar
          </Button>
          <Button
            type='submit'
            variant='contained'
            color='secondary'
            sx={{ minWidth: 120 }}
          >
            {offer ? 'Actualizar' : 'Crear'}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default OfferForm;
